/**
 * Voice — top-level sidebar group.
 *
 * Dialer + Members (provisioning) stay behind the original testing
 * allowlist/owner-only gate. Call Report is different: every floor manager
 * (14) and team lead (13) needs it to see their own team's calls (per
 * resolveVoiceCallScope in manijment-api routes.js, the backend has ALWAYS
 * scoped it correctly for them — role 13/14 just had no way to reach the
 * page, since the whole Voice group was hidden from them here). Shailendra
 * 2026-08-19, widened 2026-09-01, Call-Report-widened-to-managers/TLs
 * 2026-09-07 (reported: manager 7982526137 couldn't see the tab).
 *
 * Add a section by dropping an entry in SECTIONS + its page + route.
 */

import React from "react";
import { FormattedMessage } from "react-intl";
import { IconPhoneCall, IconUsers, IconReportAnalytics, IconRobot } from "@tabler/icons";
import { getDecryptDataFromLocal } from "../../utils/encrypt";

const phoneNumber = getDecryptDataFromLocal("phoneNumber");
const role = getDecryptDataFromLocal("setRole");
// 9268096291 = Mohd Ameen (retail/new-revenue head); 8130433974 added for
// campaign testing 2026-09-01. 8383994129, 9625366141 added 2026-09-22
// (Shailendra: give Voice/Dialer/Members/Call Report access to these too).
const VOICE_ALLOWLIST = ["9719836187", "8826345311", "9268096291", "8130433974", "8383994129", "9625366141"];
const isOwnerOrTester = VOICE_ALLOWLIST.includes(phoneNumber) || role === "1";
// 13 = team lead, 14 = floor manager, 17 = super-manager tier (Ameen already
// covered above by phone, but this also covers any future userType-17 login
// without needing a phone hardcoded here).
const isManagerOrTeamLead = ["13", "14", "17"].includes(String(role));
// Talk Time Report is a special case: ONE person, full stop — not admin,
// not any role tier (Shailendra 2026-09-11: "will be visible to 9971300423
// only ... not admin no other user"). Deliberately its own standalone check,
// not folded into VOICE_ALLOWLIST/isOwnerOrTester above — that would also
// hand this number Dialer/Members/Call Report, which wasn't asked for.
const TALK_TIME_REPORT_SOLE_VIEWER_PHONE = "9971300423";
const isTalkTimeReportViewer = phoneNumber === TALK_TIME_REPORT_SOLE_VIEWER_PHONE;

// AI Call (Bolna agent dials a number or a queue of today's leads): these three
// logins only, not all admins (Shailendra 2026-11-03). The backend enforces the
// same list (manijment-api routes.js AI_CALL_ALLOWLIST).
const AI_CALL_VIEWERS = ["9719836187", "8826345311", "9268096291"];
const isAiCallUser = AI_CALL_VIEWERS.includes(phoneNumber);

const icons = { IconPhoneCall, IconUsers, IconReportAnalytics, IconRobot };

const SECTIONS = [
  { key: "dialer", url: "/voice/dialer", icon: icons.IconPhoneCall, label: "Dialer", visible: isOwnerOrTester },
  { key: "members", url: "/voice/members", icon: icons.IconUsers, label: "Members", visible: isOwnerOrTester },
  { key: "call-report", url: "/voice/call-report", icon: icons.IconReportAnalytics, label: "Call Report", visible: isOwnerOrTester || isManagerOrTeamLead },
  { key: "ai-call", url: "/voice/ai-call", icon: icons.IconRobot, label: "AI Call", visible: isAiCallUser },
  { key: "talk-time-report", url: "/voice/talk-time-report", icon: icons.IconReportAnalytics, label: "Talk Time Report", visible: isTalkTimeReportViewer },
];

export const SHOULD_SHOW_VOICE = SECTIONS.some((s) => s.visible);

const children = SECTIONS.filter((s) => s.visible).map((s) => ({
  id: `voice-${s.key}`,
  title: <FormattedMessage id={s.label} defaultMessage={s.label} />,
  type: "item",
  url: s.url,
  icon: s.icon,
  breadcrumbs: false,
}));

export const VoiceTab = {
  id: "voice",
  title: <FormattedMessage id="Voice" defaultMessage="Voice" />,
  type: "group",
  children: children.length > 0 ? [
    {
      id: "voice-group-root",
      title: <FormattedMessage id="Voice" defaultMessage="Voice" />,
      type: "collapse",
      icon: icons.IconPhoneCall,
      children,
    },
  ] : [],
};
